import Button from 'components/Button';
import Typography from '@mui/material/Typography';
import { Column, Row } from 'further-ui/components/Layout';
import { revaluation as rules } from 'further-ui/validations';
import { useNotification } from 'hooks/ui/useNotification';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import useRevaluations from '../hooks/useRevaluations';
import RevaluationFormWithFxRate from './RevaluationFormWithFxRate';
import { HoldingClassInvestmentTotal } from './types';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';

type Props = {
  goBackAction: string;
  assetName: string;
  assetId: string;
  assetCurrency: CurrencyCode;
  vehicleId: string;
  vehicle: {
    _id: string;
    vehicleName: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  onSuccess: (data: rules.RevaluationFormType) => void;
};

const CreateRevaluationStep: React.FC<Props> = ({
  goBackAction,
  assetName,
  assetId,
  assetCurrency,
  vehicleId,
  vehicle,
  holdingClassInvestmentTotals,
  onSuccess,
}) => {
  const history = useHistory();
  const { handleSubmit, watch, control, getValues } =
    useFormContext<rules.RevaluationFormType>();
  const notification = useNotification();

  const { getAllRevaluations } = useRevaluations({ watch, control, getValues });

  const onSubmit = (data: rules.RevaluationFormType) => {
    const newRevaluations = getAllRevaluations().filter(
      (rev) => rev.isNew && !rev.isRemoved,
    );
    if (newRevaluations.length === 0) {
      notification.error('No new revaluations found.');
      return;
    }
    onSuccess(data);
  };

  const onError = (errors: FieldErrors<rules.RevaluationFormType>) => {
    notification.error(
      errors.newRevaluations?.root?.message ?? 'Something went wrong.',
    );
  };

  return (
    <RevaluationFormWithFxRate
      assetName={assetName}
      assetCurrency={assetCurrency}
      assetId={assetId}
      vehicleId={vehicleId}
      vehicle={vehicle}
      holdingClassInvestmentTotals={holdingClassInvestmentTotals}
      onSuccess={onSuccess}
      headerContent={
        <Column>
          <Typography variant="h5">
            Revalue your holding in {assetName}
          </Typography>
          <Typography variant="body2">
            Enter {assetName}'s total asset value for asset-wide revaluation, or
            select a holding class and revalue {assetName} by class.
          </Typography>
        </Column>
      }
      renderButtons={() => (
        <Row justify="space-between">
          <Button
            variant="outlined"
            size="large"
            onClick={() => history.push(goBackAction)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit(onSubmit, onError)}
          >
            Continue
          </Button>
        </Row>
      )}
    />
  );
};

export default CreateRevaluationStep;
